import { Collaborator, Folder } from '@/types';

import { apiClient } from '../http';
import { favoriteService } from './favorites';
import { toFolderMutationInput } from './resourceMutationPayloads';
import { createSanitizedResourceService, QueryParams } from './resources';

export type FolderResourceType = 'dashboards' | 'investments' | 'lists' | 'memos' | 'pipelines';

export type FolderResourceCollection = Partial<Pick<Folder, FolderResourceType>>;
export type AddFolderResourcesInput = Partial<Record<FolderResourceType, string[]>> & { destination: string };

export const toAddFolderResourcesInput = (
    folderId: string,
    resources: FolderResourceCollection,
): AddFolderResourcesInput => ({
    ...(resources.dashboards ? { dashboards: resources.dashboards.map(({ id }) => id) } : {}),
    destination: folderId,
    ...(resources.investments ? { investments: resources.investments.map(({ id }) => id) } : {}),
    ...(resources.lists ? { lists: resources.lists.map(({ id }) => id) } : {}),
    ...(resources.memos ? { memos: resources.memos.map(({ id }) => id) } : {}),
    ...(resources.pipelines ? { pipelines: resources.pipelines.map(({ id }) => id) } : {}),
});

const base = createSanitizedResourceService<
    Folder,
    ReturnType<typeof toFolderMutationInput>,
    ReturnType<typeof toFolderMutationInput>
>('/api/folders', toFolderMutationInput, toFolderMutationInput);

export const folderService = {
    ...base,
    addResources: (folderId: string, resources: FolderResourceCollection) =>
        apiClient.put<Folder, AddFolderResourcesInput>(
            `/api/folders/${folderId}/add`,
            toAddFolderResourcesInput(folderId, resources),
        ),
    getCollaborators: (folderId: string) => apiClient.get<Collaborator[]>(`/api/folders/${folderId}/collaborators`),
    list: <TParams extends QueryParams = QueryParams>(params?: TParams) => base.list(params),
    removeResource: (folderId: string, resourceType: FolderResourceType, resourceId: string) =>
        apiClient.put<Folder>(`/api/${resourceType}/${resourceId}/folders/${folderId}/remove`),
    setArchived: (id: string, archived: boolean) => base.update(id, { archived }),
    setFavorite: (id: string, favorited: boolean) => favoriteService.set<Folder>('folders', id, favorited),
    setLocked: (id: string, locked: boolean) => base.update(id, { locked }),
};
